import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Modal,
  TextField,
  Rating,
  Button,
  Fade,
  Backdrop,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { motion } from 'framer-motion';
import { ReviewContext } from '../ReviewContext';
import { showSwalNotification } from '../../utils/SwalResponse';

const MotionBox = motion.create(Box);

const UpdateReviewForm = ({ review, reload, setReload }) => {
  const { updateReview } = useContext(ReviewContext);
  const [text, setText] = useState('');
  const [rating, setRating] = useState(0);
  const [isAnonymous, setIsAnonymous] = useState(false); // Added isAnonymous state

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (review) {
      setText(review.review_text || '');
      setRating(review.rating || 0);
      setIsAnonymous(review.is_anonymous || false); // Load anonymous status from review
    }
  }, [review]);

  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!text.trim()) return;

    const updatedReview = {
      ...review,
      review_text: text.trim(),
      rating,
      is_anonymous: isAnonymous, // Include anonymous status
      updated_at: new Date().toISOString(),
    };

    const response = await updateReview(updatedReview);

    if (response?.status === 'success') {
      handleClose();
      showSwalNotification(response);
      setError(null);
      setReload(!reload);
    } else {
      setError(
        `${response?.status || 'Error'}: ${
          response?.message || 'Something went wrong.'
        }`
      );
    }
  };

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        sx={{
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        🔃 Edit
      </Button>
      <Modal
        open={open}
        handleClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 300,
          },
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={open}>
          <MotionBox
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              width: '90%',
              maxWidth: 500,
              outline: 'none',
              position: 'relative',
            }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <Typography variant="h6" mb={2}>
              Update Your Review
            </Typography>

            <TextField
              label="Your Review"
              multiline
              minRows={4}
              fullWidth
              value={text}
              onChange={(e) => setText(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                Rating:
              </Typography>
              <Rating
                name="rating"
                value={rating}
                precision={1}
                onChange={(e, newValue) => setRating(newValue)}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  color="primary"
                />
              }
              label="Post anonymously"
              sx={{ mb: 3 }}
            />

            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={handleClose} color="secondary" variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleSubmit} variant="contained" color="success">
                Save Changes
              </Button>
            </Box>
          </MotionBox>
        </Fade>
      </Modal>
    </>
  );
};

export default UpdateReviewForm;